<template>
  <div>
    <v-btn
      v-if="!filtersStore.hasUnsavedFilters"
      text
      block
      depressed
      color="primary"
      class="bg-primary"
      @click="$emit('click:close')"
    >
      {{ t('Закрыть', options) }}
    </v-btn>
    <v-btn
      v-else-if="!countDoctors && !loading"
      block
      depressed
      disabled
      color="primary"
    >
      {{ t('Не найдено', options) }}
    </v-btn>
    <v-btn
      v-else
      block
      depressed
      type="submit"
      color="primary"
      data-filters-yandex-goals="filter-doctor-list-result-show"
      :loading="loading"
      @click="$emit('click:submit')"
    >
      <template v-if="countDoctors">
        {{ t('Показать', options) }}
        {{ countDoctors.toLocaleString('ru-RU') }}
        {{ t(getNounCase(countDoctors, ['врача', 'врачей', 'врачей']), options) }}
      </template>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import I18NextVue from 'i18next'
import { getNounCase } from 'www/utils'
import apiDoctorList from 'www/vue_components/common/TheFilters/doctors/api/apiDoctorList'
import useCommonStore from 'www/vue_components/common/TheFilters/doctors/stores/useCommonStore'
import useFiltersStore from 'www/vue_components/common/TheFilters/doctors/stores/useFiltersStore'

const emit = defineEmits(['input', 'click:close', 'click:submit'])

const props = defineProps<{
  value?: boolean
  active: boolean,
  triggerRequest?: any
}>()

const commonStore = useCommonStore()
const filtersStore = useFiltersStore()

const requests = ref(0)
const loading = computed(() => Boolean(requests.value))
const countDoctors = ref<number>()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const loadDoctorsCount = async () => {
  if (!filtersStore.hasUnsavedFilters || !props.active) {
    return
  }

  requests.value++

  try {
    const {
      city,
      price,
      place,
      gender,
      science,
      disease,
      category,
      dateRange,
      speciality,
      experience,
      timesOfDay,
      patientAge,
      visitLocation,
      otherSpeciality,
      isHighRating,
      isOnlineAppointment,
      isClubAppointment,
    } = filtersStore.unsavedFilters

    const getPageTypeParams = () => {
      if (visitLocation === 'online') {
        return {
          town_id: city?.id,
          page_type: 'online_doctor_list',
        } as const
      }

      const newTown = place?.townRedirect
      const targetTown = place?.isMetro ? commonStore.town.regionalCenter : commonStore.town

      if (visitLocation === 'home') {
        return {
          page_type: 'house_doctor_list',
          town_id: newTown?.id || targetTown.id,
        } as const
      }

      const hasPlace = place && !newTown

      return {
        page_type: 'town_doctor_list',
        town_id: newTown?.id || targetTown.id,
        metro_id: hasPlace && place.isMetro ? place.id : undefined,
        district_id: hasPlace && !place.isMetro ? place.id : undefined,
      } as const
    }

    const { data } = await apiDoctorList.getCount({
      ...getPageTypeParams(),
      date_start_visit: dateRange?.[0],
      date_end_visit: dateRange?.[1] || dateRange?.[0],
      time_visit: timesOfDay,
      patient_age: patientAge,
      high_rating: isHighRating,
      disease_id: disease?.id,
      speciality_id: speciality.id,
      other_speciality_id: otherSpeciality?.id,
      ...(visitLocation === 'clinic' && {
        online_appointment: isOnlineAppointment,
        club_appointment: isClubAppointment,
      }),
      ...(visitLocation !== 'home' && {
        price_from: price.from,
        price_to: price.to,
      }),
      gender,
      science,
      category,
      experience,
    })

    countDoctors.value = data.count
  } catch {
    countDoctors.value = undefined
  } finally {
    requests.value--
  }
}

if (props.triggerRequest !== undefined) {
  watch(() => props.triggerRequest, loadDoctorsCount)
} else {
  watch(() => filtersStore.unsavedFilters, loadDoctorsCount)
}

const isActiveButton = computed(() => Boolean(filtersStore.hasUnsavedFilters && countDoctors.value && !loading.value))

watch(isActiveButton, () => emit('input', isActiveButton.value))
</script>
